import * as XLSX from 'xlsx';

// ─── Types (mirror from InvoiceStatistics.tsx) ────────────────────────────────
export interface ItemStat {
  itemName: string;
  totalQty: number;
  totalRevenue: number;
  invoiceCount: number;
  options: Record<string, { name: string; qty: number; revenue: number }>;
}

export interface OptionStat {
  optionName: string;
  itemName: string;
  totalQty: number;
  totalRevenue: number;
}

export interface PaymentStat {
  method: string;
  count: number;
  revenue: number;
}

export interface DayStat {
  date: string;
  revenue: number;
  invoiceCount: number;
  guestCount: number;
}

export interface HourStat {
  hour: number;
  invoiceCount: number;
  revenue: number;
}

export interface StatisticsExportData {
  // KPI
  totalInvoiceCount: number;
  totalRevenue: number;
  avgRevenuePerInvoice: number;
  totalGuests: number;
  avgGuestPerInvoice: string;
  totalItems: number;
  // Date range label for the report header
  dateRangeLabel: string;
  // Detail data
  revenueByDay: DayStat[];
  itemStatsByMost: ItemStat[];
  optionStats: OptionStat[];
  paymentStats: PaymentStat[];
  hourStats: HourStat[];
}

// ─── Style helpers ────────────────────────────────────────────────────────────
/** Mỗi cell style phải là object riêng, không share reference giữa các cell */
const makeStyle = (overrides: Record<string, any> = {}) => ({
  font: { name: 'Arial', sz: 10, ...overrides.font },
  alignment: { vertical: 'center', wrapText: true, ...overrides.alignment },
  border: {
    top:    { style: 'thin', color: { rgb: 'D0D0D0' } },
    bottom: { style: 'thin', color: { rgb: 'D0D0D0' } },
    left:   { style: 'thin', color: { rgb: 'D0D0D0' } },
    right:  { style: 'thin', color: { rgb: 'D0D0D0' } },
    ...overrides.border,
  },
  fill: overrides.fill,
  numFmt: overrides.numFmt,
});

const HEADER_STYLE = makeStyle({
  font: { name: 'Arial', sz: 10, bold: true, color: { rgb: 'FFFFFF' } },
  fill: { patternType: 'solid', fgColor: { rgb: '1890FF' } },
  alignment: { horizontal: 'center', vertical: 'center' },
});

const TITLE_STYLE = makeStyle({
  font: { name: 'Arial', sz: 13, bold: true, color: { rgb: '1890FF' } },
  alignment: { horizontal: 'left', vertical: 'center' },
  border: {},
  fill: undefined,
});

const SECTION_STYLE = makeStyle({
  font: { name: 'Arial', sz: 10, bold: true, color: { rgb: '262626' } },
  fill: { patternType: 'solid', fgColor: { rgb: 'F5F5F5' } },
  alignment: { horizontal: 'left', vertical: 'center' },
});

const NUMBER_STYLE = makeStyle({
  numFmt: '#,##0',
  alignment: { horizontal: 'right', vertical: 'center' },
});

const LABEL_STYLE = makeStyle({
  font: { name: 'Arial', sz: 10, bold: true },
  fill: { patternType: 'solid', fgColor: { rgb: 'F0F7FF' } },
  alignment: { horizontal: 'left', vertical: 'center' },
});

const PCT_STYLE = makeStyle({
  numFmt: '0.0"%"',
  alignment: { horizontal: 'right', vertical: 'center' },
});

const fmtVND = (v: number) => v.toLocaleString('vi-VN') + ' ₫';

// ─── Utility: apply styles to a range of cells ───────────────────────────────
function applyStyle(ws: XLSX.WorkSheet, range: string, style: object) {
  const r = XLSX.utils.decode_range(range);
  for (let R = r.s.r; R <= r.e.r; R++) {
    for (let C = r.s.c; C <= r.e.c; C++) {
      const addr = XLSX.utils.encode_cell({ r: R, c: C });
      if (!ws[addr]) ws[addr] = { t: 'z', v: '' };
      ws[addr].s = makeStyle((style as any));
    }
  }
}

function setCellStyle(ws: XLSX.WorkSheet, addr: string, style: object) {
  if (!ws[addr]) ws[addr] = { t: 'z', v: '' };
  ws[addr].s = style;
}

function setCell(ws: XLSX.WorkSheet, addr: string, value: any, style?: object, type?: string) {
  const t = type ?? (typeof value === 'number' ? 'n' : 's');
  ws[addr] = { v: value, t, s: style ?? makeStyle() };
}

// ─── Sheet 1: Tổng quan KPI ──────────────────────────────────────────────────
function buildKpiSheet(data: StatisticsExportData): XLSX.WorkSheet {
  const ws: XLSX.WorkSheet = {};
  const cols = ['A', 'B', 'C'];

  // Title
  ws['A1'] = { v: 'BÁO CÁO THỐNG KÊ HÓA ĐƠN — 8AM COFFEE', t: 's', s: TITLE_STYLE };
  ws['A2'] = { v: `Khoảng thời gian: ${data.dateRangeLabel}`, t: 's', s: makeStyle({ font: { name: 'Arial', sz: 10, italic: true, color: { rgb: '595959' } }, border: {} }) };
  ws['A3'] = { v: `Xuất lúc: ${new Date().toLocaleString('vi-VN')}`, t: 's', s: makeStyle({ font: { name: 'Arial', sz: 10, italic: true, color: { rgb: '595959' } }, border: {} }) };

  // KPI Section header row
  ws['A5'] = { v: 'CHỈ SỐ', t: 's', s: HEADER_STYLE };
  ws['B5'] = { v: 'GIÁ TRỊ', t: 's', s: HEADER_STYLE };
  ws['C5'] = { v: 'GHI CHÚ', t: 's', s: HEADER_STYLE };

  const kpiRows: [string, any, string][] = [
    ['Tổng hóa đơn',        data.totalInvoiceCount,       'Số hóa đơn duy nhất'],
    ['Tổng doanh thu',       data.totalRevenue,             'VND'],
    ['Trung bình / hóa đơn', data.avgRevenuePerInvoice,    'VND'],
    ['Tổng lượt khách',      data.totalGuests,              'Người'],
    ['Trung bình khách / HĐ',Number(data.avgGuestPerInvoice),'Người/hóa đơn'],
    ['Tổng lượt món',        data.totalItems,               'Lần gọi món'],
  ];

  kpiRows.forEach(([label, value, note], i) => {
    const row = 6 + i;
    ws[`A${row}`] = { v: label, t: 's', s: LABEL_STYLE };
    ws[`B${row}`] = { v: value, t: 'n', s: NUMBER_STYLE };
    ws[`C${row}`] = { v: note,  t: 's', s: makeStyle() };
  });

  // Payment stats sub-section
  let row = 13;
  ws[`A${row}`] = { v: 'PHÂN BỔ PHƯƠNG THỨC THANH TOÁN', t: 's', s: SECTION_STYLE };
  ws[`B${row}`] = { v: '', t: 's', s: SECTION_STYLE };
  ws[`C${row}`] = { v: '', t: 's', s: SECTION_STYLE };
  row++;

  ws[`A${row}`] = { v: 'Phương thức', t: 's', s: HEADER_STYLE };
  ws[`B${row}`] = { v: 'Số HĐ',       t: 's', s: HEADER_STYLE };
  ws[`C${row}`] = { v: 'Doanh thu',    t: 's', s: HEADER_STYLE };
  row++;

  data.paymentStats.forEach(pm => {
    const pct = data.totalInvoiceCount > 0 ? (pm.count / data.totalInvoiceCount * 100).toFixed(1) : '0';
    ws[`A${row}`] = { v: `${pm.method} (${pct}%)`, t: 's', s: makeStyle() };
    ws[`B${row}`] = { v: pm.count,   t: 'n', s: NUMBER_STYLE };
    ws[`C${row}`] = { v: pm.revenue, t: 'n', s: NUMBER_STYLE };
    row++;
  });

  // Hour stats sub-section
  row++;
  ws[`A${row}`] = { v: 'KHUNG GIỜ CAO ĐIỂM (TOP 10)', t: 's', s: SECTION_STYLE };
  ws[`B${row}`] = { v: '', t: 's', s: SECTION_STYLE };
  ws[`C${row}`] = { v: '', t: 's', s: SECTION_STYLE };
  row++;

  ws[`A${row}`] = { v: 'Khung giờ', t: 's', s: HEADER_STYLE };
  ws[`B${row}`] = { v: 'Số HĐ',     t: 's', s: HEADER_STYLE };
  ws[`C${row}`] = { v: 'Doanh thu', t: 's', s: HEADER_STYLE };
  row++;

  const topHours = [...data.hourStats].sort((a, b) => b.invoiceCount - a.invoiceCount).slice(0, 10);
  topHours.forEach(h => {
    ws[`A${row}`] = { v: `${String(h.hour).padStart(2, '0')}:00 – ${String(h.hour + 1).padStart(2, '0')}:00`, t: 's', s: makeStyle() };
    ws[`B${row}`] = { v: h.invoiceCount, t: 'n', s: NUMBER_STYLE };
    ws[`C${row}`] = { v: h.revenue,      t: 'n', s: NUMBER_STYLE };
    row++;
  });

  ws['!ref'] = `A1:C${row}`;
  ws['!cols'] = [{ wch: 36 }, { wch: 20 }, { wch: 22 }];
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 2 } }, // Title
    { s: { r: 1, c: 0 }, e: { r: 1, c: 2 } }, // Date range
    { s: { r: 2, c: 0 }, e: { r: 2, c: 2 } }, // Export time
  ];
  ws['!rows'] = [{ hpt: 28 }, { hpt: 18 }, { hpt: 18 }];

  return ws;
}

// ─── Sheet 2: Doanh thu theo ngày ────────────────────────────────────────────
function buildDaySheet(data: StatisticsExportData): XLSX.WorkSheet {
  const headers = ['Ngày', 'Số hóa đơn', 'Số khách', 'Doanh thu (VND)', '% so với ngày cao nhất'];
  const maxRev = Math.max(...data.revenueByDay.map(d => d.revenue), 1);

  const rows = data.revenueByDay.map(d => [
    d.date,
    d.invoiceCount,
    d.guestCount,
    d.revenue,
    d.revenue / maxRev * 100,
  ]);

  // Tổng cộng dòng cuối
  rows.push([
    'TỔNG CỘNG',
    data.revenueByDay.reduce((s, d) => s + d.invoiceCount, 0),
    data.revenueByDay.reduce((s, d) => s + d.guestCount, 0),
    data.revenueByDay.reduce((s, d) => s + d.revenue, 0),
    100,
  ]);

  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

  // Style headers
  ['A1','B1','C1','D1','E1'].forEach(addr => {
    ws[addr].s = HEADER_STYLE;
  });

  // Style data rows
  rows.forEach((_, i) => {
    const rowIdx = i + 2;
    const isTotal = i === rows.length - 1;
    const baseStyle = isTotal ? SECTION_STYLE : makeStyle();
    const numStyle  = isTotal ? SECTION_STYLE : NUMBER_STYLE;
    const pctStyle  = isTotal ? SECTION_STYLE : PCT_STYLE;

    if (ws[`A${rowIdx}`]) ws[`A${rowIdx}`].s = baseStyle;
    if (ws[`B${rowIdx}`]) ws[`B${rowIdx}`].s = numStyle;
    if (ws[`C${rowIdx}`]) ws[`C${rowIdx}`].s = numStyle;
    if (ws[`D${rowIdx}`]) ws[`D${rowIdx}`].s = numStyle;
    if (ws[`E${rowIdx}`]) ws[`E${rowIdx}`].s = pctStyle;
  });

  ws['!cols'] = [{ wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 22 }, { wch: 22 }];
  return ws;
}

// ─── Sheet 3: Xếp hạng món ───────────────────────────────────────────────────
function buildItemSheet(data: StatisticsExportData): XLSX.WorkSheet {
  const headers = [
    'Hạng', 'Tên món', 'Lượt gọi', 'Xuất hiện trong HĐ',
    'Doanh thu (VND)', 'TB / lần gọi (VND)', 'Số loại option',
    '% so với món cao nhất',
  ];

  const maxQty = data.itemStatsByMost[0]?.totalQty || 1;

  const rows = data.itemStatsByMost.map((item, i) => [
    i + 1,
    item.itemName,
    item.totalQty,
    item.invoiceCount,
    item.totalRevenue,
    item.totalQty > 0 ? Math.round(item.totalRevenue / item.totalQty) : 0,
    Object.keys(item.options).length,
    item.totalQty / maxQty * 100,
  ]);

  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

  // Style headers
  ['A1','B1','C1','D1','E1','F1','G1','H1'].forEach(addr => {
    if (ws[addr]) ws[addr].s = HEADER_STYLE;
  });

  // Style data rows
  rows.forEach((_, i) => {
    const r = i + 2;
    const isTop3 = i < 3;
    const rankStyle = isTop3
      ? makeStyle({ font: { name: 'Arial', sz: 10, bold: true, color: { rgb: '1890FF' } } })
      : makeStyle();

    if (ws[`A${r}`]) ws[`A${r}`].s = rankStyle;
    if (ws[`B${r}`]) ws[`B${r}`].s = isTop3
      ? makeStyle({ font: { name: 'Arial', sz: 10, bold: true } })
      : makeStyle();
    if (ws[`C${r}`]) ws[`C${r}`].s = NUMBER_STYLE;
    if (ws[`D${r}`]) ws[`D${r}`].s = NUMBER_STYLE;
    if (ws[`E${r}`]) ws[`E${r}`].s = NUMBER_STYLE;
    if (ws[`F${r}`]) ws[`F${r}`].s = NUMBER_STYLE;
    if (ws[`G${r}`]) ws[`G${r}`].s = makeStyle({ alignment: { horizontal: 'center', vertical: 'center' } });
    if (ws[`H${r}`]) ws[`H${r}`].s = PCT_STYLE;
  });

  ws['!cols'] = [
    { wch: 7 }, { wch: 36 }, { wch: 12 }, { wch: 20 },
    { wch: 22 }, { wch: 22 }, { wch: 16 }, { wch: 22 },
  ];
  return ws;
}

// ─── Sheet 4: Phân tích option ───────────────────────────────────────────────
function buildOptionSheet(data: StatisticsExportData): XLSX.WorkSheet {
  const headers = ['Hạng', 'Tên option', 'Thuộc món', 'Lượt chọn', 'Doanh thu (VND)'];

  const rows = data.optionStats.map((opt, i) => [
    i + 1,
    opt.optionName,
    opt.itemName,
    opt.totalQty,
    opt.totalRevenue,
  ]);

  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

  ['A1','B1','C1','D1','E1'].forEach(addr => {
    if (ws[addr]) ws[addr].s = HEADER_STYLE;
  });

  rows.forEach((_, i) => {
    const r = i + 2;
    if (ws[`A${r}`]) ws[`A${r}`].s = makeStyle({ alignment: { horizontal: 'center', vertical: 'center' } });
    if (ws[`B${r}`]) ws[`B${r}`].s = makeStyle();
    if (ws[`C${r}`]) ws[`C${r}`].s = makeStyle({ font: { name: 'Arial', sz: 10, color: { rgb: '595959' } } });
    if (ws[`D${r}`]) ws[`D${r}`].s = NUMBER_STYLE;
    if (ws[`E${r}`]) ws[`E${r}`].s = NUMBER_STYLE;
  });

  ws['!cols'] = [{ wch: 7 }, { wch: 32 }, { wch: 32 }, { wch: 14 }, { wch: 22 }];
  return ws;
}

// ─── Main export function ─────────────────────────────────────────────────────
export function exportStatisticsToExcel(data: StatisticsExportData): void {
  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, buildKpiSheet(data),    'Tong quan');
  XLSX.utils.book_append_sheet(wb, buildDaySheet(data),    'Doanh thu theo ngay');
  XLSX.utils.book_append_sheet(wb, buildItemSheet(data),   'Xep hang mon');
  XLSX.utils.book_append_sheet(wb, buildOptionSheet(data), 'Phan tich option');

  const dateTag = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  XLSX.writeFile(wb, `baocao-8amcoffee-${dateTag}.xlsx`);
}
